import classNames from '@/common/utils/classNames';
import { DsDrawer } from '@/modules/doctorHome/components/DsDrawer';
import { Check, ChevronDown } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { PanelTextButton } from '../../../components/ui';

export const PanelCheckbox = ({ checked, onChange, label }: { checked: boolean; onChange: (checked: boolean) => void; label: string }) => (
  <button type="button" role="checkbox" aria-checked={checked} onClick={() => onChange(!checked)} className="flex min-h-[44px] w-full items-center gap-3 rounded-xl py-2 text-start transition-colors duration-150 active:bg-gray-50">
    <span
      className={classNames(
        'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px] transition-colors duration-150',
        checked ? 'border-primary bg-primary text-white' : 'border-[#c4c4c4] bg-white',
      )}
      aria-hidden
    >
      {checked ? (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
          <path d="M5 12.5L9.5 17L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : null}
    </span>
    <span className="text-[15px] font-medium text-[#222]">{label}</span>
  </button>
);

/** Sheet content: right-aligned title, body, stacked buttons. */
export const SheetBody = ({ title, children, footer }: { title: string; children: ReactNode; footer?: ReactNode }) => (
  <div className="px-4 pb-4" dir="rtl">
    <h2 className="pb-3 pt-1 text-[18px] font-bold text-[#222]">{title}</h2>
    {children}
    {footer ? <div className="mt-5 flex flex-col gap-1">{footer}</div> : null}
  </div>
);

/** Cancel action: shared accent text button, dimmed and inert while an async step is running. */
export const CancelButton = ({ onClick, busy }: { onClick: () => void; busy?: boolean }) => (
  <div className={classNames('flex flex-col', busy && 'pointer-events-none opacity-50')} aria-disabled={busy}>
    <PanelTextButton onClick={busy ? undefined : onClick}>انصراف</PanelTextButton>
  </div>
);

/** Soft gray pill that opens a bottom-sheet radio list (replaces a native select). */
export const PillPicker = ({
  label,
  valueLabel,
  options,
  value,
  onChange,
}: {
  label: string;
  valueLabel: string;
  options: { value: number; label: string }[];
  value: number;
  onChange: (value: number) => void;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        aria-label={label}
        onClick={() => setOpen(true)}
        className="flex h-9 min-w-0 items-center gap-1 rounded-full bg-gray-100 px-3.5 text-[14px] font-medium text-[#222] transition-colors duration-150 active:bg-gray-200"
      >
        <span className="truncate tabular-nums">{valueLabel}</span>
        <ChevronDown size={16} strokeWidth={1.8} className="shrink-0 text-[#8a8a8a]" aria-hidden />
      </button>
      <DsDrawer open={open} onOpenChange={setOpen} title={label} level={1}>
        <SheetBody title={label}>
          <ul className="max-h-[60vh] overflow-y-auto">
            {options.map(option => {
              const active = option.value === value;
              return (
                <li key={option.value} className="border-b border-black/[0.06] last:border-0">
                  <button
                    type="button"
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                    className="flex min-h-[48px] w-full items-center justify-between gap-3 py-3 text-start transition-colors duration-150 active:bg-gray-50"
                  >
                    <span className={classNames('text-[15px]', active ? 'font-semibold text-primary' : 'text-[#222]')}>{option.label}</span>
                    {active ? <Check size={20} strokeWidth={1.8} className="shrink-0 text-primary" aria-hidden /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </SheetBody>
      </DsDrawer>
    </>
  );
};
